"use client";

import { useContext } from "react";
import { FeatureFlagContext } from "./provider";
import type { FeatureFlags } from "./types";
import { DEFAULT_FEATURE_FLAGS } from "./defaults";

export function useFeatureFlags() {
  const context = useContext(FeatureFlagContext);
  if (context === undefined) {
    throw new Error("useFeatureFlags must be used within a FeatureFlagProvider");
  }
  return context;
}

export function useFeatureFlag(flagName: keyof FeatureFlags) {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flagName);
}

// Entity convenience hooks
export function useNewTasksImplementation() {
  return useFeatureFlag("useNewTasksImplementation");
}

export function useNewContactsImplementation() {
  return useFeatureFlag("useNewContactsImplementation");
}

export function useNewTimeEntriesImplementation() {
  return useFeatureFlag("useNewTimeEntriesImplementation");
}

export function useNewTicketsImplementation() {
  return useFeatureFlag("useNewTicketsImplementation");
}

export function useNewCompaniesImplementation() {
  return useFeatureFlag("useNewCompaniesImplementation");
}

export function useNewContractsImplementation() {
  return useFeatureFlag("useNewContractsImplementation");
}

export function useNewUsersImplementation() {
  return useFeatureFlag("useNewUsersImplementation");
}

export function useNewReportsImplementation() {
  return useFeatureFlag("useNewReportsImplementation");
}

export function useNewMessagesImplementation() {
  return useFeatureFlag("useNewMessagesImplementation");
}

export function useGenericForms() {
  return useFeatureFlag("useGenericForms");
}

export function useGenericTableComponents() {
  return useFeatureFlag("useGenericTableComponents");
}

// Utility functions (non-hook, exported here for co-location)
export function shouldUseNewImplementation(entityName: string): boolean {
  const flagMap: Record<string, keyof FeatureFlags> = {
    tasks: "useNewTasksImplementation",
    contacts: "useNewContactsImplementation",
    time_entries: "useNewTimeEntriesImplementation",
    tickets: "useNewTicketsImplementation",
    companies: "useNewCompaniesImplementation",
    contracts: "useNewContractsImplementation",
    users: "useNewUsersImplementation",
    reports: "useNewReportsImplementation",
    messages: "useNewMessagesImplementation",
  };

  const flagName = flagMap[entityName];
  if (!flagName) return false;
  return DEFAULT_FEATURE_FLAGS[flagName];
}

export function getEnabledTableImplementations(): string[] {
  return Object.entries(DEFAULT_FEATURE_FLAGS)
    .filter(([key, value]) => key.startsWith("useNew") && key.endsWith("Implementation") && value)
    .map(([key]) => key.replace("useNew", "").replace("Implementation", "").toLowerCase());
}

export function getDisabledTableImplementations(): string[] {
  return Object.entries(DEFAULT_FEATURE_FLAGS)
    .filter(([key, value]) => key.startsWith("useNew") && key.endsWith("Implementation") && !value)
    .map(([key]) => key.replace("useNew", "").replace("Implementation", "").toLowerCase());
}
